/**
 * DataSourceBrowser - Browse and map fields from data sources
 * 
 * A clean interface for exploring data source structure (buckets, measurements, fields)
 * with smart search that works across all levels. Includes background prefetching
 * for instant field search.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { ScrollArea } from "@/shared/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  Database,
  Table2,
  Hash,
  Tag,
  ChevronDown,
  ChevronRight,
  Loader2,
  Search,
  RefreshCw,
  AlertCircle,
  Layers,
  ChevronUp,
} from "lucide-react";
import type { DataSource, DataSourceType } from "@/core/api/data-sources";
import { getDataSourceTypeInfo } from "@/core/api/data-sources";
import { FieldMappingDialog } from "./FieldMappingDialog";

// =============================================================================
// Types
// =============================================================================

interface FieldInfo {
  name: string;
  type: string;
}

interface TagInfo {
  key: string;
  values: string[];
}

interface DataSourceBrowserProps {
  dataSources: DataSource[];
  factoryId: string;
  selectedSourceId?: string;
  onSourceChange?: (sourceId: string) => void;
}

interface SearchMatch {
  bucket: string;
  measurement: string;
  matchingFields: string[];
  measurementMatches: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const INITIAL_VISIBLE_MEASUREMENTS = 20;
const BATCH_SIZE = 5;

// =============================================================================
// Component
// =============================================================================

export function DataSourceBrowser({
  dataSources,
  factoryId,
  selectedSourceId: controlledSourceId,
  onSourceChange,
}: DataSourceBrowserProps) {
  const queryClient = useQueryClient();
  
  // State
  const [internalSourceId, setInternalSourceId] = useState<string>("");
  const [selectedBucket, setSelectedBucket] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedMeasurements, setExpandedMeasurements] = useState<Set<string>>(new Set());
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_MEASUREMENTS);
  
  // Field mapping dialog
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [mappingField, setMappingField] = useState<{
    name: string;
    type: string;
    measurement: string;
    bucket: string;
  } | null>(null);

  // Prefetch state
  const [allFieldsCache, setAllFieldsCache] = useState<Record<string, FieldInfo[]>>({});
  const [isPreloadingFields, setIsPreloadingFields] = useState(false);

  // Use controlled or internal source id
  const selectedSourceId = controlledSourceId ?? internalSourceId;
  const setSelectedSourceId = useCallback((id: string) => {
    if (controlledSourceId !== undefined) {
      onSourceChange?.(id);
    } else {
      setInternalSourceId(id);
    }
  }, [controlledSourceId, onSourceChange]);

  // Get active data sources
  const activeDataSources = dataSources.filter(ds => ds.is_active);
  const selectedSource = dataSources.find(ds => ds.id === selectedSourceId);
  const sourceType = selectedSource?.source_type as DataSourceType | undefined;
  
  // Determine if this is a relational database
  const isRelationalDB = sourceType === "postgresql" || sourceType === "mysql";

  // Reset when source changes
  useEffect(() => {
    setSelectedBucket("");
    setAllFieldsCache({});
    setSearchQuery("");
    setExpandedMeasurements(new Set());
    setVisibleCount(INITIAL_VISIBLE_MEASUREMENTS);
  }, [selectedSourceId]);

  // Reset when bucket changes
  useEffect(() => {
    setAllFieldsCache({});
    setSearchQuery("");
    setExpandedMeasurements(new Set());
    setVisibleCount(INITIAL_VISIBLE_MEASUREMENTS);
  }, [selectedBucket]);

  // =============================================================================
  // Queries
  // =============================================================================

  // Fetch buckets/schemas
  const { 
    data: buckets = [], 
    isLoading: loadingBuckets, 
    error: bucketsError,
    refetch: refetchBuckets,
  } = useQuery({
    queryKey: ["data-browser", selectedSourceId, isRelationalDB ? "schemas" : "buckets"],
    queryFn: async () => {
      if (isRelationalDB) {
        const functionName = sourceType === "mysql" ? "mysql-browser" : "postgresql-browser";
        const { data, error } = await supabase.functions.invoke(functionName, {
          body: { action: "schemas", dataSourceId: selectedSourceId },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return data.data as string[];
      } else {
        const { data, error } = await supabase.functions.invoke("influxdb-browser", {
          body: { action: "buckets", dataSourceId: selectedSourceId },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return data.data as string[];
      }
    },
    enabled: !!selectedSourceId && !!selectedSource,
  });

  // Fetch measurements/tables
  const { 
    data: measurements = [], 
    isLoading: loadingMeasurements, 
    error: measurementsError,
    refetch: refetchMeasurements,
  } = useQuery({
    queryKey: ["data-browser", selectedSourceId, selectedBucket, isRelationalDB ? "tables" : "measurements"],
    queryFn: async () => {
      if (isRelationalDB) {
        const functionName = sourceType === "mysql" ? "mysql-browser" : "postgresql-browser";
        const { data, error } = await supabase.functions.invoke(functionName, {
          body: { action: "tables", dataSourceId: selectedSourceId, schema: selectedBucket },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return (data.data as { name: string }[]).map(t => t.name);
      } else {
        const { data, error } = await supabase.functions.invoke("influxdb-browser", {
          body: { action: "measurements", dataSourceId: selectedSourceId, bucket: selectedBucket },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return data.data as string[];
      }
    },
    enabled: !!selectedSourceId && !!selectedBucket,
  });

  // =============================================================================
  // Background Prefetching
  // =============================================================================

  useEffect(() => {
    if (!measurements.length || !selectedSourceId || !selectedBucket) {
      return;
    }

    const preloadAllFields = async () => {
      setIsPreloadingFields(true);
      const cache: Record<string, FieldInfo[]> = {};

      // Batch fetch fields for all measurements
      for (let i = 0; i < measurements.length; i += BATCH_SIZE) {
        const batch = measurements.slice(i, i + BATCH_SIZE);
        const results = await Promise.all(
          batch.map(async (measurement) => {
            try {
              if (isRelationalDB) {
                const functionName = sourceType === "mysql" ? "mysql-browser" : "postgresql-browser";
                const { data, error } = await supabase.functions.invoke(functionName, {
                  body: { action: "columns", dataSourceId: selectedSourceId, schema: selectedBucket, table: measurement },
                });
                if (error || data?.error) return { measurement, fields: [] };
                return {
                  measurement,
                  fields: (data.data as { name: string; dataType: string }[]).map(col => ({
                    name: col.name,
                    type: col.dataType,
                  })),
                };
              } else {
                const { data, error } = await supabase.functions.invoke("influxdb-browser", {
                  body: { action: "fields", dataSourceId: selectedSourceId, bucket: selectedBucket, measurement },
                });
                if (error || data?.error) return { measurement, fields: [] };
                return { measurement, fields: data.data as FieldInfo[] };
              }
            } catch {
              return { measurement, fields: [] };
            }
          })
        );

        results.forEach(({ measurement, fields }) => {
          cache[measurement] = fields;
        });

        // Update cache incrementally for better UX
        setAllFieldsCache(prev => ({ ...prev, ...cache }));
      }

      setIsPreloadingFields(false);
    };

    preloadAllFields();
  }, [measurements, selectedSourceId, selectedBucket, isRelationalDB, sourceType]);

  // =============================================================================
  // Smart Search & Filtering
  // =============================================================================

  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return measurements.map(m => ({
        measurement: m,
        matchingFields: [] as string[],
        measurementMatches: true,
      }));
    }

    const query = searchQuery.toLowerCase();
    return measurements
      .map(measurement => {
        const measurementMatches = measurement.toLowerCase().includes(query);
        const fields = allFieldsCache[measurement] || [];
        const matchingFields = fields
          .filter(f => f.name.toLowerCase().includes(query))
          .map(f => f.name);

        return {
          measurement,
          matchingFields,
          measurementMatches,
          hasMatch: measurementMatches || matchingFields.length > 0,
        };
      })
      .filter(item => item.hasMatch);
  }, [measurements, searchQuery, allFieldsCache]);

  // Auto-expand measurements with matching fields when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      const measurementsWithFieldMatches = filteredResults
        .filter(item => item.matchingFields.length > 0)
        .map(item => item.measurement);

      setExpandedMeasurements(new Set(measurementsWithFieldMatches));
    }
  }, [searchQuery, filteredResults]);

  // Visible results with "show more"
  const visibleResults = filteredResults.slice(0, visibleCount);
  const hasMore = filteredResults.length > visibleCount;

  // =============================================================================
  // Handlers
  // =============================================================================

  const toggleMeasurement = (measurement: string) => {
    setExpandedMeasurements(prev => {
      const next = new Set(prev);
      if (next.has(measurement)) {
        next.delete(measurement);
      } else {
        next.add(measurement);
      }
      return next;
    });
  };

  const toggleTag = (tagKey: string) => {
    setExpandedTags(prev => {
      const next = new Set(prev);
      if (next.has(tagKey)) {
        next.delete(tagKey);
      } else {
        next.add(tagKey);
      }
      return next;
    });
  };

  const handleRefresh = () => {
    if (selectedBucket) {
      setAllFieldsCache({});
      refetchMeasurements();
    } else if (selectedSourceId) {
      refetchBuckets();
    }
  };

  const handleMapField = (measurement: string, fieldName: string, fieldType: string) => {
    setMappingField({
      name: fieldName,
      type: fieldType,
      measurement,
      bucket: selectedBucket,
    });
    setMappingDialogOpen(true);
  };

  const handleShowMore = () => {
    setVisibleCount(prev => prev + INITIAL_VISIBLE_MEASUREMENTS);
  };

  const handleShowLess = () => {
    setVisibleCount(INITIAL_VISIBLE_MEASUREMENTS);
  };

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <>
      <div className="flex flex-col h-full min-h-0 gap-4">
        {/* Source & Bucket Selectors */}
        <div className="space-y-3">
          {/* Data Source */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              Data Source
            </label>
            <div className="flex gap-2">
              <Select value={selectedSourceId} onValueChange={(v) => setSelectedSourceId(v)}>
                <SelectTrigger className="flex-1 focus:ring-offset-0">
                  <SelectValue placeholder="Select a data source" />
                </SelectTrigger>
                <SelectContent>
                  {activeDataSources.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">
                      No active data sources
                    </div>
                  ) : (
                    activeDataSources.map((ds) => {
                      const typeInfo = getDataSourceTypeInfo(ds.source_type as DataSourceType);
                      return (
                        <SelectItem key={ds.id} value={ds.id}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${typeInfo.bgColor}`} />
                            {ds.name}
                          </div>
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleRefresh} 
                disabled={!selectedSourceId}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Bucket / Schema */}
          {selectedSourceId && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" />
                {isRelationalDB ? "Schema" : "Bucket"}
              </label>
              {loadingBuckets ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground p-2 border rounded-md">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading {isRelationalDB ? "schemas" : "buckets"}...
                </div>
              ) : bucketsError ? (
                <div className="flex items-center gap-2 text-sm text-destructive p-2 border border-destructive/30 rounded-md">
                  <AlertCircle className="h-4 w-4" />
                  {(bucketsError as Error).message}
                </div>
              ) : (
                <Select value={selectedBucket} onValueChange={setSelectedBucket}>
                  <SelectTrigger className="focus:ring-offset-0">
                    <SelectValue placeholder={`Select a ${isRelationalDB ? "schema" : "bucket"}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {buckets.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground text-center">
                        No {isRelationalDB ? "schemas" : "buckets"} found
                      </div>
                    ) : (
                      buckets.map((bucket) => (
                        <SelectItem key={bucket} value={bucket}>
                          <div className="flex items-center gap-2">
                            <Layers className="h-4 w-4 text-muted-foreground" />
                            {bucket}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        {/* Measurements Browser */}
        {selectedBucket && (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Header with search */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">
                  {isRelationalDB ? "Tables & Columns" : "Measurements & Fields"}
                </label>
                {isPreloadingFields && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Indexing...
                  </span>
                )}
              </div>
              <Badge variant="secondary" className="text-xs">
                {searchQuery
                  ? `${filteredResults.length} / ${measurements.length}`
                  : `${measurements.length} found`}
              </Badge>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={
                  isPreloadingFields
                    ? "Indexing fields for search..."
                    : `Search ${isRelationalDB ? "tables and columns" : "measurements and fields"}...`
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                disabled={isPreloadingFields && !Object.keys(allFieldsCache).length}
              />
            </div>

            {/* Results */}
            <ScrollArea className="flex-1 min-h-0 border rounded-lg">
              {loadingMeasurements ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : measurementsError ? (
                <div className="flex flex-col items-center justify-center p-8 text-destructive">
                  <AlertCircle className="h-6 w-6 mb-2" />
                  <p className="text-sm">{(measurementsError as Error).message}</p>
                </div>
              ) : measurements.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
                  <Table2 className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">No {isRelationalDB ? "tables" : "measurements"} found</p>
                </div>
              ) : filteredResults.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-6 text-muted-foreground">
                  <Search className="h-6 w-6 mb-2 opacity-50" />
                  <p className="text-sm">No matches for "{searchQuery}"</p>
                  {isPreloadingFields && (
                    <p className="text-xs mt-1">Still indexing fields...</p>
                  )}
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {visibleResults.map(({ measurement, matchingFields }) => (
                    <MeasurementItem
                      key={measurement}
                      measurement={measurement}
                      isExpanded={expandedMeasurements.has(measurement)}
                      onToggle={() => toggleMeasurement(measurement)}
                      dataSourceId={selectedSourceId}
                      bucket={selectedBucket}
                      factoryId={factoryId}
                      isRelationalDB={isRelationalDB}
                      sourceType={sourceType}
                      cachedFields={allFieldsCache[measurement]}
                      searchQuery={searchQuery}
                      highlightedFields={matchingFields}
                      expandedTags={expandedTags}
                      onToggleTag={toggleTag}
                      onMapField={(fieldName, fieldType) => handleMapField(measurement, fieldName, fieldType)}
                    />
                  ))}

                  {/* Show more / less */}
                  {(hasMore || visibleCount > INITIAL_VISIBLE_MEASUREMENTS) && (
                    <div className="flex items-center justify-center gap-2 pt-2 border-t mt-2">
                      {hasMore && (
                        <Button variant="ghost" size="sm" onClick={handleShowMore}>
                          <ChevronDown className="h-4 w-4 mr-1" />
                          Show more ({filteredResults.length - visibleCount} remaining)
                        </Button>
                      )}
                      {visibleCount > INITIAL_VISIBLE_MEASUREMENTS && (
                        <Button variant="ghost" size="sm" onClick={handleShowLess}>
                          <ChevronUp className="h-4 w-4 mr-1" />
                          Show less
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        )}

        {/* Empty State */}
        {!selectedSourceId && (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <Database className="h-12 w-12 mb-4 opacity-30" />
            <p className="text-sm">Select a data source to browse</p>
          </div>
        )}

        {selectedSourceId && !selectedBucket && !loadingBuckets && !bucketsError && (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <Layers className="h-12 w-12 mb-4 opacity-30" />
            <p className="text-sm">Select a {isRelationalDB ? "schema" : "bucket"} to browse</p>
          </div>
        )}
      </div>

      {/* Field Mapping Dialog */}
      {mappingField && selectedSourceId && (
        <FieldMappingDialog
          open={mappingDialogOpen}
          onOpenChange={setMappingDialogOpen}
          factoryId={factoryId}
          dataSourceId={selectedSourceId}
          bucketName={mappingField.bucket}
          measurement={mappingField.measurement}
          fieldName={mappingField.name}
          fieldType={mappingField.type}
        />
      )}
    </>
  );
}

// =============================================================================
// MeasurementItem Component
// =============================================================================

interface MeasurementItemProps {
  measurement: string;
  isExpanded: boolean;
  onToggle: () => void;
  dataSourceId: string;
  bucket: string;
  factoryId: string;
  isRelationalDB: boolean;
  sourceType?: DataSourceType;
  cachedFields?: FieldInfo[];
  searchQuery?: string;
  highlightedFields?: string[];
  expandedTags: Set<string>;
  onToggleTag: (key: string) => void;
  onMapField?: (fieldName: string, fieldType: string) => void;
}

function MeasurementItem({
  measurement,
  isExpanded,
  onToggle,
  dataSourceId,
  bucket,
  factoryId,
  isRelationalDB,
  sourceType,
  cachedFields,
  searchQuery = "",
  highlightedFields = [],
  expandedTags,
  onToggleTag,
  onMapField,
}: MeasurementItemProps) {
  // Fetch fields if not cached (only when expanded)
  const { data: fetchedFields, isLoading: loadingFields } = useQuery({
    queryKey: ["data-browser-fields", dataSourceId, bucket, measurement],
    queryFn: async () => {
      if (isRelationalDB) {
        const functionName = sourceType === "mysql" ? "mysql-browser" : "postgresql-browser";
        const { data, error } = await supabase.functions.invoke(functionName, {
          body: { action: "columns", dataSourceId, schema: bucket, table: measurement },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return (data.data as { name: string; dataType: string }[]).map(col => ({
          name: col.name,
          type: col.dataType,
        }));
      } else {
        const { data, error } = await supabase.functions.invoke("influxdb-browser", {
          body: { action: "fields", dataSourceId, bucket, measurement },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return data.data as FieldInfo[];
      }
    },
    enabled: isExpanded && !cachedFields,
    initialData: cachedFields,
  });

  const fieldsData = cachedFields || fetchedFields || [];

  // Fetch tags (only for InfluxDB)
  const { data: tagsData, isLoading: loadingTags } = useQuery({
    queryKey: ["data-browser-tags", dataSourceId, bucket, measurement],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("influxdb-browser", {
        body: { action: "tags", dataSourceId, bucket, measurement },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data.data as TagInfo[];
    },
    enabled: isExpanded && !isRelationalDB,
  });

  // Fetch field mappings to check which fields are already mapped
  const { data: fieldMappings = [] } = useQuery({
    queryKey: ["field-mappings-check", factoryId, dataSourceId, bucket, measurement],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("field_mappings")
        .select("field_name")
        .eq("factory_id", factoryId)
        .eq("data_source_id", dataSourceId)
        .eq("bucket_name", bucket)
        .eq("measurement", measurement);
      if (error) throw error;
      return data.map(m => m.field_name);
    },
    enabled: isExpanded && !!factoryId && !!dataSourceId && !!bucket,
  });

  const isFieldMapped = (fieldName: string) => fieldMappings.includes(fieldName);
  const isFieldHighlighted = (fieldName: string) => highlightedFields.includes(fieldName);

  // Filter fields based on search query
  const visibleFields = searchQuery
    ? fieldsData.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : fieldsData;

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 p-2 hover:bg-muted/50 transition-colors text-left"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <Table2 className="h-4 w-4 text-chart-2 shrink-0" />
        <span className="text-sm font-medium truncate flex-1">{measurement}</span>
        {highlightedFields.length > 0 && (
          <Badge variant="outline" className="text-xs text-primary border-primary/30">
            {highlightedFields.length} match{highlightedFields.length !== 1 ? "es" : ""}
          </Badge>
        )}
      </button>

      {isExpanded && (
        <div className="border-t bg-muted/30 p-2 space-y-2">
          {(loadingFields || loadingTags) && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading schema...
            </div>
          )}

          {/* Fields */}
          {visibleFields.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                <Hash className="h-3 w-3" />
                {isRelationalDB ? "Columns" : "Fields"} ({visibleFields.length})
                {highlightedFields.length > 0 && (
                  <span className="text-primary">
                    {" "}• {highlightedFields.length} match{highlightedFields.length !== 1 ? "es" : ""}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {visibleFields.map((field) => {
                  const mapped = isFieldMapped(field.name);
                  const highlighted = isFieldHighlighted(field.name);
                  return (
                    <Badge
                      key={field.name}
                      variant="outline"
                      className={`text-xs cursor-pointer transition-colors ${
                        mapped
                          ? "bg-success/20 border-success text-success hover:bg-success/30"
                          : highlighted
                          ? "bg-primary/20 border-primary text-primary hover:bg-primary/30 ring-1 ring-primary/50"
                          : "hover:bg-primary/10 hover:border-primary/50"
                      }`}
                      onClick={() => onMapField?.(field.name, field.type)}
                      title={
                        mapped
                          ? "Already mapped - click to add another mapping"
                          : "Click to map to factory structure"
                      }
                    >
                      {field.name}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tags (InfluxDB only) */}
          {tagsData && tagsData.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                <Tag className="h-3 w-3" />
                Tags ({tagsData.length})
              </div>
              <div className="space-y-1">
                {tagsData.map((tag) => (
                  <div key={tag.key} className="border rounded bg-background">
                    <button
                      onClick={() => onToggleTag(tag.key)}
                      className="w-full flex items-center gap-1 p-1 text-xs hover:bg-muted/50"
                    >
                      {expandedTags.has(tag.key) ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                      <span className="font-medium">{tag.key}</span>
                      <span className="text-muted-foreground">({tag.values.length})</span>
                    </button>
                    {expandedTags.has(tag.key) && tag.values.length > 0 && (
                      <div className="p-1 pt-0 flex flex-wrap gap-1">
                        {tag.values.slice(0, 20).map((value) => (
                          <Badge key={value} variant="secondary" className="text-xs font-normal">
                            {value}
                          </Badge>
                        ))}
                        {tag.values.length > 20 && (
                          <Badge variant="outline" className="text-xs">
                            +{tag.values.length - 20} more
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loadingFields && visibleFields.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-2">
              No {isRelationalDB ? "columns" : "fields"} found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
